// Remote catalog — code-defined and MF-exposed (via ./remoteRegistry), not
// runtime appsettings: nothing here is secret (it all reaches the browser),
// and a reviewed, type-checked array beats a fetched blob while the contract
// is evolving. If per-environment URLs ever require build-once-promote-many,
// swap the SOURCE of configureRemotes() — callers don't change.
//
// Expose names are NOT configurable: every routes-mode remote publishes
// exactly ./routes and ./i18nMessages (see remote-registry.ts). That is why
// there is no `routesExpose` field.
export interface RemoteConfig {
  id: string;
  remoteName: string;
  alias?: string;
  entry: string;
  entryType?: "module" | "global";
  routePrefixes: string[];
  preload?: boolean;
  requiresAuthByDefault?: boolean;
  /**
   * OneID permission name required to enter this remote's routes (checked as
   * `can("manage", <name>)` against the rbac ability). Merged into each
   * registered route's meta unless the route sets its own.
   */
  requiresPermission?: string;
}

export const defaultRemotes: RemoteConfig[] = [
  {
    id: "inboundIntelligence",
    remoteName: "shipbob_inbound_intelligence_ui",
    alias: "shipbob_inbound_intelligence_ui",
    // Deployed dev URL, not localhost: this is the committed default that a
    // deployed host uses. Local dev overrides it via
    // public/appsettings.local.json remoteEntries (gitignored), and each
    // environment's own appsettings.json carries that environment's URL — see
    // applyRemoteEntryOverrides. URLs are the one env-specific piece of the
    // catalog, so they stay runtime-editable.
    entry: "https://inboundintelligenceui-dev.shipbob.dev/mf-manifest.json",
    entryType: "module",
    routePrefixes: ["/ni/inbound"],
    preload: false,
    requiresAuthByDefault: true,
  },
];

/**
 * Applies per-environment entry-URL overrides (appsettings `remoteEntries`,
 * keyed by remote id) over a code-defined catalog. Structure stays code;
 * URLs stay ops-editable without a rebuild.
 */
export function applyRemoteEntryOverrides(
  remotes: RemoteConfig[],
  remoteEntries: Record<string, string> | undefined,
): RemoteConfig[] {
  if (!remoteEntries) return remotes;
  return remotes.map((remote) => {
    const entry = remoteEntries[remote.id];
    return entry ? { ...remote, entry } : remote;
  });
}
